import { useQuery } from "@tanstack/react-query";
import HttpService from "@/hooks/Https-services";
import { getCurrentAuth } from "@/hooks/useAxiosPrivate";
import { APIRoutes } from "@/utils/constants";
import type { PaymentAlertSubscription, PendingPayment } from "@/utils/paymentRecovery";

function currentUserId(): string | null {
  const userId = getCurrentAuth()?.UserID;
  return userId != null && userId !== "" ? String(userId) : null;
}

// Both endpoints return the subscription and the outstanding invoice, but the
// casing differs (subscription/Subscription, pendingPayment/PendingPayment).
const pickSub = (res: any): PaymentAlertSubscription | null =>
  res?.data?.data?.subscription ?? res?.data?.data?.Subscription ?? null;
const pickPending = (res: any): PendingPayment | null =>
  res?.data?.data?.pendingPayment ?? res?.data?.data?.PendingPayment ?? null;

interface SubscriptionRecoveryData {
  subscription: PaymentAlertSubscription | null;
  pendingPayment: PendingPayment | null;
}

export interface SubscriptionRecovery extends SubscriptionRecoveryData {
  isLoading: boolean;
  // True while refetchOnMount:"always" is revalidating, even when isLoading
  // is already false because a (possibly stale) cached value exists. Callers
  // that gate a "no subscription to recover" decision on `subscription`
  // should wait on this too, or they can briefly act on stale cached status
  // (e.g. a just-cleared invoice's old "incomplete") before the real state
  // lands.
  isFetching: boolean;
}

/**
 * HTPTV-695 (v2): the viewer's live subscription plus any outstanding open
 * invoice. Reads /subscription-status (which returns the subscription
 * regardless of status, including past_due/incomplete - the users we target),
 * falling back to getUserAccountDetails. Powers the header PaymentAlert and the
 * "Complete Payment" plan CTA. NOT get-user-subscription-details, which gates on
 * PaymentStatus="paid" and returns null for exactly the failing subs we need.
 */
export function useSubscriptionRecovery(): SubscriptionRecovery {
  const token = getCurrentAuth()?.AccessToken;
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["v2", "subscription-recovery", currentUserId()],
    enabled: Boolean(token),
    // Payment state changes on other pages (after settling an invoice, etc.),
    // so always reflect the true state on mount rather than a 30s-stale cache.
    refetchOnMount: "always",
    queryFn: async ({ signal }): Promise<SubscriptionRecoveryData> => {
      try {
        const res = await HttpService.get(APIRoutes.SubscriptionStatus, signal);
        return { subscription: pickSub(res), pendingPayment: pickPending(res) };
      } catch (err) {
        // A cancelled request (unmount / refetch) rejects with an AbortError;
        // don't treat that as a real failure and fire a second doomed request
        // against the same already-aborted signal.
        if (signal.aborted) throw err;
        const res = await HttpService.get(APIRoutes.GetUserAccountDetails, signal);
        return { subscription: pickSub(res), pendingPayment: pickPending(res) };
      }
    },
  });
  return {
    subscription: data?.subscription ?? null,
    pendingPayment: data?.pendingPayment ?? null,
    isLoading: Boolean(token) && isLoading,
    isFetching: Boolean(token) && isFetching,
  };
}
